import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  BookOpen, FileText, Video, FileImage, Presentation,
  CheckCircle2, Circle, ArrowLeft, ArrowRight, Download,
} from "lucide-react";

const CATEGORY_LABEL = {
  onboarding: "Onboarding",
  product: "Product / Project",
  skills: "Sales Skills",
  compliance: "Compliance",
  ai_coaching: "AI Coaching",
};

const MODULE_TYPE_META = {
  text: { label: "Text", icon: FileText, color: "bg-slate-500" },
  video: { label: "Video", icon: Video, color: "bg-red-500" },
  pdf: { label: "PDF", icon: FileImage, color: "bg-blue-500" },
  slide: { label: "Slides", icon: Presentation, color: "bg-amber-500" },
};

// Convert a YouTube/Loom/Vimeo URL to its embed form
const toEmbedUrl = (url) => {
  if (!url) return null;
  const u = url.trim();
  try {
    if (/youtu\.be\//.test(u)) {
      const id = u.split("youtu.be/")[1].split(/[?&]/)[0];
      return `https://www.youtube.com/embed/${id}`;
    }
    if (/youtube\.com\/watch/.test(u)) {
      const id = new URL(u).searchParams.get("v");
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (/youtube\.com\/embed\//.test(u)) return u;
    if (/loom\.com\/share\//.test(u)) {
      const id = u.split("loom.com/share/")[1].split(/[?&]/)[0];
      return `https://www.loom.com/embed/${id}`;
    }
    if (/vimeo\.com\/(\d+)/.test(u)) {
      const id = u.match(/vimeo\.com\/(\d+)/)[1];
      return `https://player.vimeo.com/video/${id}`;
    }
    if (/player\.vimeo\.com/.test(u)) return u;
  } catch { /* ignore */ }
  return null;
};

const TrainingCatalogPage = () => {
  const { courseId } = useParams();
  if (courseId) return <CoursePlayer courseId={courseId} />;
  return <Catalog />;
};

const Catalog = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [myProgress, setMyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [tab, setTab] = useState("catalog");

  const load = async () => {
    setLoading(true);
    try {
      const [cs, me] = await Promise.all([
        axios.get(`${API}/training/courses`),
        axios.get(`${API}/training/my-progress`),
      ]);
      setCourses(cs.data?.courses || []);
      setMyProgress(me.data?.courses || []);
    } catch { toast.error("Failed to load training"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const set = new Set(courses.map((c) => c.category));
    return ["all", ...Array.from(set)];
  }, [courses]);

  const filtered = useMemo(() => (
    category === "all" ? courses : courses.filter((c) => c.category === category)
  ), [category, courses]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="training-catalog-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-violet-600" /> Training
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Learn the product, sharpen your pitch, stay compliant.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="training-tabs">
            <TabsTrigger value="catalog" data-testid="tab-catalog">Catalog</TabsTrigger>
            <TabsTrigger value="my" data-testid="tab-my-progress">My Training</TabsTrigger>
          </TabsList>

          <TabsContent value="catalog" className="space-y-4 mt-4">
            <div className="flex gap-1 flex-wrap">
              {categories.map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${
                          category === c
                            ? "bg-violet-600 text-white border-violet-600"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                        data-testid={`cat-filter-${c}`}>
                  {c === "all" ? "All" : (CATEGORY_LABEL[c] || c)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-16 text-slate-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-400">No published courses in this category yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((c) => (
                  <Card key={c.course_id} className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/training/course/${c.course_id}`)}
                        data-testid={`course-tile-${c.course_id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 leading-tight">{c.title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                              {CATEGORY_LABEL[c.category] || c.category}
                            </Badge>
                            <span className="text-[11px] text-slate-500">{c.total_modules} modules</span>
                          </div>
                        </div>
                        {c.status === "completed" && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                      </div>
                      {c.description && <p className="text-xs text-slate-600 line-clamp-2">{c.description}</p>}
                      {c.total_modules > 0 && (
                        <div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-violet-500" style={{ width: `${c.progress_pct || 0}%` }} />
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1">
                            {c.completed_modules}/{c.total_modules} complete
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my" className="space-y-3 mt-4">
            {myProgress.length === 0 ? (
              <div className="text-center py-16 text-slate-400">You haven't started any course yet.</div>
            ) : (
              <div className="space-y-2" data-testid="my-progress-list">
                {myProgress.map((p) => (
                  <Card key={p.course_id} className="border-slate-200 hover:shadow-sm transition cursor-pointer"
                        onClick={() => navigate(`/training/course/${p.course_id}`)}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-violet-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{p.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <Badge className="bg-slate-100 text-slate-700 text-[10px]">{CATEGORY_LABEL[p.category] || p.category}</Badge>
                          <Badge className={p.status === "completed"
                            ? "bg-emerald-100 text-emerald-700 text-[10px]"
                            : "bg-amber-100 text-amber-800 text-[10px]"}>
                            {p.status === "completed" ? "Completed" : `${p.progress_pct}%`}
                          </Badge>
                          <span className="text-[11px] text-slate-500">{p.completed_modules}/{p.total_modules} modules</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

const CoursePlayer = ({ courseId }) => {
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/training/courses/${courseId}`);
      setCourse(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load course");
    }
  };
  useEffect(() => { load(); }, [courseId]);

  if (!course) return <Layout><div className="p-6 text-slate-500">Loading course…</div></Layout>;

  const modules = course.modules || [];
  const active = modules[activeIdx];
  const completedCount = modules.filter((m) => m.completed).length;
  const pct = modules.length ? Math.round((completedCount / modules.length) * 100) : 0;

  const markComplete = async () => {
    if (!active) return;
    setCompleting(true);
    try {
      const r = await axios.post(`${API}/training/courses/${courseId}/modules/${active.module_id}/complete`);
      toast.success(r.data.course_complete ? "Course complete! 🎉" : "Marked complete");
      await load();
      if (activeIdx < modules.length - 1) setActiveIdx(activeIdx + 1);
    } catch {
      toast.error("Failed to save");
    } finally { setCompleting(false); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-4" data-testid="course-player-page">
        <button onClick={() => navigate("/training")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to catalog
        </button>

        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div>
              <p className="text-xl font-bold text-slate-900">{course.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {modules.length} modules · {completedCount} completed · {pct}% done
              </p>
            </div>
            <div className="flex-1 max-w-md">
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Module list */}
          <Card className="border-slate-200 lg:col-span-1 self-start">
            <CardContent className="p-3 space-y-1" data-testid="module-list">
              {modules.map((m, i) => {
                const meta = MODULE_TYPE_META[m.type] || MODULE_TYPE_META.text;
                const Icon = meta.icon;
                const isActive = i === activeIdx;
                return (
                  <button key={m.module_id} onClick={() => setActiveIdx(i)}
                          className={`w-full text-left p-2 rounded-lg flex items-center gap-2 text-xs ${
                            isActive ? "bg-violet-50 border border-violet-200"
                                     : "hover:bg-slate-50 border border-transparent"
                          }`}
                          data-testid={`module-nav-${m.module_id}`}>
                    <div className={`w-6 h-6 rounded ${meta.color} flex items-center justify-center shrink-0`}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="flex-1 truncate">{m.title}</span>
                    {m.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                                 : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Player */}
          <Card className="border-slate-200 lg:col-span-3">
            <CardContent className="p-5 space-y-4">
              {active ? (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wide">
                        Module {activeIdx + 1} of {modules.length}
                      </p>
                      <p className="text-lg font-semibold text-slate-900">{active.title}</p>
                    </div>
                    {active.completed && <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>}
                  </div>

                  <ModuleContent module={active} />

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <Button variant="outline" size="sm" disabled={activeIdx === 0}
                            onClick={() => setActiveIdx(activeIdx - 1)}>
                      <ArrowLeft className="w-4 h-4 mr-1" /> Previous
                    </Button>
                    <div className="flex gap-2">
                      {!active.completed && (
                        <Button onClick={markComplete} disabled={completing}
                                data-testid="mark-complete-btn"
                                className="bg-violet-600 hover:bg-violet-700 text-white">
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          {completing ? "Saving…" : "Mark Complete"}
                        </Button>
                      )}
                      <Button variant="outline" size="sm" disabled={activeIdx >= modules.length - 1}
                              onClick={() => setActiveIdx(activeIdx + 1)}>
                        Next <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-slate-400 text-center py-10">This course has no modules yet.</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

const ModuleContent = ({ module: m }) => {
  if (m.type === "text") {
    return (
      <div className="prose prose-sm max-w-none whitespace-pre-wrap text-slate-700"
           data-testid="module-content-text">
        {m.content_text}
      </div>
    );
  }
  if (m.type === "video") {
    const src = toEmbedUrl(m.video_url);
    if (!src) return <a href={m.video_url} target="_blank" rel="noreferrer" className="text-violet-600 underline">Open video ↗</a>;
    return (
      <div className="aspect-video bg-black rounded-lg overflow-hidden" data-testid="module-content-video">
        <iframe src={src} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen title={m.title} />
      </div>
    );
  }
  if (m.type === "pdf" || m.type === "slide") {
    // PDFs embed natively in iframe. For PPT/PPTX, browser forces download.
    const fileSrc = `${API}/training/files/${m.file_path}`;
    const isPdf = (m.file_name || "").toLowerCase().endsWith(".pdf")
               || (m.type === "pdf");
    if (isPdf) {
      return (
        <div className="h-[65vh] border border-slate-200 rounded-lg overflow-hidden" data-testid="module-content-pdf">
          <iframe src={fileSrc} className="w-full h-full" title={m.title} />
        </div>
      );
    }
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center" data-testid="module-content-slide">
        <Presentation className="w-10 h-10 text-amber-500 mx-auto mb-2" />
        <p className="font-semibold text-slate-900">{m.file_name}</p>
        <p className="text-xs text-slate-500 mt-1 mb-3">PPT/PPTX can't preview inline — download to view.</p>
        <a href={fileSrc} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 text-sm bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700">
          <Download className="w-4 h-4" /> Download
        </a>
      </div>
    );
  }
  return null;
};

export default TrainingCatalogPage;
